import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Modal,
    Form,
    InputNumber,
    message,
    Popconfirm,
    Card,
    Row,
    Col,
    Tooltip,
    Tag,
    Typography
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    ReloadOutlined,
    CopyOutlined,
    KeyOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ApiService } from '../services/api';
import {
    FlashWriterAuthCode,
    FlashWriterAuthCodeListRequest,
    CreateFlashWriterAuthCodeRequest
} from '../types';

const { Text } = Typography;

const FlashWriterAuthCodeManagement: React.FC = () => {
    const [authCodes, setAuthCodes] = useState<FlashWriterAuthCode[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [total, setTotal] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const pageSize = 20;

    // 模态框状态
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [form] = Form.useForm();

    // 获取授权码列表
    const fetchAuthCodes = async (page: number = currentPage) => {
        setLoading(true);
        try {
            const request: FlashWriterAuthCodeListRequest = {
                page,
                page_size: pageSize
            };
            const response = await ApiService.getFlashWriterAuthCodeList(request);
            if (response.code === 200) {
                setAuthCodes(response.data.auth_codes || []);
                setTotal(response.data.total);
                setCurrentPage(page);
            } else {
                message.error(response.message || '获取授权码列表失败');
            }
        } catch (error) {
            console.error('获取授权码列表失败:', error);
            message.error('获取授权码列表失败');
        } finally {
            setLoading(false);
        }
    };

    // 页面加载时获取数据
    useEffect(() => {
        fetchAuthCodes(1);
    }, []);

    // 处理新增授权码
    const handleAdd = () => {
        setIsModalVisible(true);
        form.resetFields();
        form.setFieldsValue({ valid_hours: 24 }); // 默认24小时
    };

    // 处理删除授权码
    const handleDelete = async (id: number) => {
        try {
            const response = await ApiService.deleteFlashWriterAuthCode({ id });
            if (response.code === 200) {
                message.success(response.message || '授权码删除成功');
                fetchAuthCodes(currentPage);
            } else {
                message.error(response.message || '授权码删除失败');
            }
        } catch (error) {
            console.error('删除授权码失败:', error);
            message.error('删除授权码失败');
        }
    };

    // 复制授权码到剪贴板
    const handleCopy = (authCode: string) => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(authCode).then(() => {
                message.success('授权码已复制到剪贴板');
            }).catch(() => {
                message.error('复制失败，请手动复制');
            });
        } else {
            // 降级方案：使用传统的复制方法
            const textArea = document.createElement('textarea');
            textArea.value = authCode;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                message.success('授权码已复制到剪贴板.');
            } catch (err) {
                message.error('复制失败，请手动复制');
            }
            document.body.removeChild(textArea);
        }
    };

    // 处理模态框确认
    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            const request: CreateFlashWriterAuthCodeRequest = {
                valid_hours: values.valid_hours
            };
            const response = await ApiService.createFlashWriterAuthCode(request);
            if (response.code === 200) {
                message.success(response.message || '授权码创建成功');
                setIsModalVisible(false);
                fetchAuthCodes(1); // 新增后回到第一页

                // 显示新创建的授权码
                Modal.success({
                    title: '授权码创建成功',
                    content: (
                        <div style={{ marginTop: 16 }}>
                            <p>授权码：</p>
                            <div style={{
                                background: '#f5f5f5',
                                padding: '12px 16px',
                                borderRadius: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <Text strong style={{ fontSize: 18, letterSpacing: 2 }}>
                                    {response.data.auth_code}
                                </Text>
                                <Button
                                    type="link"
                                    icon={<CopyOutlined />}
                                    onClick={() => handleCopy(response.data.auth_code)}
                                >
                                    复制
                                </Button>
                            </div>
                            <p style={{ marginTop: 12, color: '#666' }}>
                                到期时间：{new Date(response.data.expires_at_datetime).toLocaleString('zh-CN')}
                            </p>
                        </div>
                    ),
                    okText: '确定',
                    width: 480
                });
            } else {
                message.error(response.message || '授权码创建失败');
            }
        } catch (error) {
            console.error('提交失败:', error);
        }
    };

    // 处理模态框取消
    const handleModalCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    // 检查授权码是否过期
    const isExpired = (expiresAt: number): boolean => {
        return expiresAt * 1000 < Date.now();
    };

    // 表格列定义
    const columns: ColumnsType<FlashWriterAuthCode> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80
        },
        {
            title: '授权码',
            dataIndex: 'auth_code',
            key: 'auth_code',
            width: 180,
            render: (text: string) => (
                <Space>
                    <KeyOutlined style={{ color: '#1890ff' }} />
                    <Text code style={{ fontSize: 14, letterSpacing: 1 }}>{text}</Text>
                    <Tooltip title="复制授权码">
                        <Button
                            type="link"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopy(text)}
                            style={{ padding: 0 }}
                        />
                    </Tooltip>
                </Space>
            )
        },
        {
            title: '到期时间',
            dataIndex: 'expires_at_datetime',
            key: 'expires_at_datetime',
            width: 180,
            render: (text: string, record: FlashWriterAuthCode) => {
                const expired = isExpired(record.expires_at);
                return (
                    <Space>
                        <span>{new Date(text).toLocaleString('zh-CN')}</span>
                        {expired ? (
                            <Tag color="red">已过期</Tag>
                        ) : (
                            <Tag color="green">有效</Tag>
                        )}
                    </Space>
                );
            }
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (text: string) => new Date(text).toLocaleString('zh-CN')
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Space size="small">
                    <Popconfirm
                        title="确认删除"
                        description="确定要删除这个授权码吗？此操作不可恢复。"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Card>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <h2 style={{ margin: 0 }}>Flash Writer 授权码管理</h2>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => fetchAuthCodes(currentPage)}
                                loading={loading}
                            >
                                刷新
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleAdd}
                            >
                                新增授权码
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={authCodes}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: false,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
                        onChange: (page) => fetchAuthCodes(page),
                    }}
                    size="middle"
                />
            </Card>

            <Modal
                title="新增授权码"
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText="确定"
                cancelText="取消"
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    style={{ marginTop: 16 }}
                >
                    <Form.Item
                        label="有效时长（小时）"
                        name="valid_hours"
                        rules={[
                            { required: true, message: '请输入有效时长' },
                            { type: 'number', min: 1, max: 8760, message: '有效时长必须在1-8760小时之间' }
                        ]}
                        extra="授权码的有效期，范围1-8760小时（最多一年）"
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="请输入有效时长"
                            min={1}
                            max={8760}
                            addonAfter="小时"
                        />
                    </Form.Item>

                    <div style={{
                        background: '#f0f5ff',
                        padding: 12,
                        borderRadius: 4,
                        marginTop: 16
                    }}>
                        <p style={{ margin: 0, color: '#666' }}>
                            <strong>提示：</strong>
                            授权码将自动生成12位随机字符串（包含大写字母A-Z和数字0-9）
                        </p>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default FlashWriterAuthCodeManagement;

